import type { ShallowRef, MaybeRefOrGetter, Ref } from 'vue';

export type WistiaVideoInstance = {
  bind: (eventName: string, callback: (...args: Array<unknown>) => void) => void;
  unbind: (eventName: string, callback?: (...args: Array<unknown>) => void) => void;
  play: () => void;
  pause: () => void;
};

declare global {
  interface Window {
    _wq?: Array<{
      id: string;
      onReady: (video: WistiaVideoInstance) => void;
    }>;
  }
}

export const useWistiaVideo = ({
  hashedId,
}: {
  hashedId: MaybeRefOrGetter<string | undefined>;
}): {
  isReady: Ref<boolean>;
  isPlaying: Ref<boolean>;
  videoInstance: ShallowRef<WistiaVideoInstance | null>;
} => {
  useHead({
    script: [
      {
        key: 'wistia-script',
        src: 'https://fast.wistia.com/assets/external/E-v1.js',
        async: true,
      },
    ],
  });

  const isReady = ref(false);
  const isPlaying = ref(false);
  const videoInstance = shallowRef<WistiaVideoInstance | null>(null);

  watch(
    () => toValue(hashedId),
    (currentHashedId, _previousHashedId, onCleanup) => {
      isReady.value = false;
      isPlaying.value = false;
      videoInstance.value = null;

      if (!currentHashedId || !import.meta.client) {
        return;
      }

      window._wq = window._wq ?? [];
      window._wq.push({
        id: currentHashedId,
        onReady: (video) => {
          videoInstance.value = video;
          isReady.value = true;
          video.bind('play', () => {
            isPlaying.value = true;
          });
          video.bind('pause', () => {
            isPlaying.value = false;
          });
        },
      });

      onCleanup(() => {
        isReady.value = false;
        isPlaying.value = false;
        videoInstance.value = null;
      });
    },
    { immediate: true }
  );

  return { isReady, isPlaying, videoInstance };
};
